import { useEffect, useRef } from "react"
import type React from "react"
import { useForm, Controller } from "react-hook-form"
import styles from "./UtilityForm.module.scss"
import TextInput from "../../../../common/components/xepp/text-input/TextInput"
import Button from "../../../../common/components/xepp/button/Button"
import ToggleSlider from "../../../../common/components/xepp/toggle-slider/ToggleSlider"
import { UtilityIcon } from "../../../../assets/icons"
import { useGetCountriesQuery } from "../../../../service/utility-service"
import type { Utility } from "../../../../model/bill-settings/bill-settings"

interface Country {
  id: number
  isDeleted: boolean
  name: string
  imageUrl: string
  isoCode: string
}

interface UtilityFormProps {
  utility: Utility
  /**
   * Function to handle cancel action
   */
  onCancel: () => void

  /**
   * Function to handle saving a new utility
   */
  onSave: (data: any) => void
  isSaving: boolean;
}

export enum TariffTypes {
  postpaid = "Postpaid",
  prepaid = "Prepaid",
  both = "Both"
}

interface FormValues {
  id: string
  name: string
  imageFile: File | null
  imageUrl: string | null
  countryId: number
  utilityCode: string
  chargeType: "fixed" | "percentage"
  percentageCharge: string
  fixedCharge: string
  tariff: TariffTypes
}

const UtilityForm: React.FC<UtilityFormProps> = ({ utility, onCancel, onSave, isSaving }) => {
  const { data: countries = [], isLoading: countriesLoading } = useGetCountriesQuery();
  const fileInputRef = useRef<HTMLInputElement>(null)
  const numberRegex = /^[0-9]*\.?[0-9]*$/;
  
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    clearErrors
  } = useForm<FormValues>({
    defaultValues: {
      id: utility?.id || "",
      name: utility?.name || "",
      imageFile: null,
      imageUrl: utility?.imageUrl || null,
      countryId: utility?.countryId || 0,
      utilityCode: utility?.utilityCode || "",
      chargeType: utility?.tariffPlan?.percentageCharge ? "percentage" : "fixed",
      percentageCharge: utility?.tariffPlan?.percentageCharge?.toString() || "",
      fixedCharge: utility?.tariffPlan?.fixedCharge?.toString() || "",
      tariff: utility?.tariffPlan?.tariff?.toLowerCase() as TariffTypes ?? "Postpaid",
    },
  })

  const watchName = watch("name")
  const watchImageFile = watch("imageFile")
  const watchCountryId = watch("countryId")
  const watchTariff = watch("tariff")
  const watchChargeType = watch("chargeType")

  useEffect(() => {
    if (utility) {
      setValue("id", utility.id || "")
      setValue("name", utility.name || "")
      setValue("imageUrl", utility.imageUrl || null)
      setValue("countryId", utility.countryId || 0)
      setValue("utilityCode", utility.utilityCode || "")
      setValue("percentageCharge", utility.tariffPlan?.percentageCharge?.toString() || "")
      setValue("fixedCharge", utility.tariffPlan?.fixedCharge?.toString() || "")
      setValue("chargeType", utility?.tariffPlan?.percentageCharge ? "percentage" : "fixed")
      setValue("tariff", utility?.tariffPlan?.tariff as TariffTypes ?? "Postpaid")
    }
  }, [utility, setValue])

  useEffect(() => {
    if (watchChargeType === "percentage") {
      clearErrors("fixedCharge")
    } else {
      clearErrors("percentageCharge")
    }
  }, [watchChargeType, setValue, clearErrors])

  const handleImageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null
    if (file) {
      setValue("imageFile", file)
    }
  }

  const handleCountrySelect = (countryId: number) => {
    setValue("countryId", countryId)
    clearErrors("countryId")
  }

  const getInitials = () => {
    if (!watchName) return "CA"
    const words = watchName.split(" ")
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase()
    return (words[0][0] + words[1][0]).toUpperCase()
  }

  const getDisplayPictureUrl = () => {
    if (watchImageFile) {
      return URL.createObjectURL(watchImageFile)
    }
    return utility?.imageUrl || null
  }

  const onSubmit = async (data: FormValues) => {
    if (!data.countryId) {
      setValue("countryId", 0)
      return
    }

    if (data.chargeType === "percentage" && !data.percentageCharge) {
      return
    }


    if (data.chargeType === "fixed" && !data.fixedCharge) {
      return
    }

	let imageToSubmit = data.imageFile;

    const submitData = {
      ...data,
	  imageFile: imageToSubmit,
      percentageCharge: data.chargeType === "percentage" ? data.percentageCharge : undefined,
      fixedCharge: data.chargeType === "fixed" ? data.fixedCharge : undefined,
    }

    onSave(submitData)
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.iconContainer}>
          <UtilityIcon />
        </div>
        <h2 className={styles.title}>Edit Utility</h2>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className={styles.bodyContainer}>
        <div className={styles.formContainer}>
          <div className={styles.displayPictureSection}>
            <span className={styles.sectionLabel}>Add Display Picture</span>
            <div className={styles.displayPictureContainer}>
              <input
                type="file"
                id="imageFile"
                ref={fileInputRef}
                accept="image/jpeg,image/png,image/gif"
                onChange={handleImageFileChange}
                className={styles.fileInput}
              />
              <label htmlFor="imageFile" className={styles.displayPictureLabel}>
                {getDisplayPictureUrl() ? (
                  <img
                    src={getDisplayPictureUrl() ?? "/placeholder.svg"}
                    alt="Display"
                    className={styles.displayPictureImage}
                  />
                ) : (
                  <div className={styles.displayPicturePlaceholder}>{getInitials()}</div>
                )}
              </label>
            </div>
          </div>

          <Controller
            name="name"
            control={control}
            rules={{ required: "Utility name is required" }}
            render={({ field, fieldState }) => (
              <TextInput
                label="Utility Name"
                value={field.value}
                onChange={field.onChange}
                placeholder="Enter utility name"
                error={fieldState.error?.message ?? ""}
              />
            )}
          />

          <div className={styles.formDivider}></div>

          <div className={`${styles.formSection} ${styles.row}`}>
            <span className={styles.sectionLabel}>Transaction Charges</span>
            <div className={`${styles.radioGroup} ${styles.column}`}>
              <label className={styles.radioLabel}>
                <Controller
                  name="chargeType"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="radio"
                      checked={field.value === "fixed"}
                      onChange={() => field.onChange("fixed")}
                      className={styles.radioInput}
                    />
                  )}
                />
                <span className={styles.radioText}>Fixed</span>
              </label>
              <label className={styles.radioLabel}>
                <Controller
                  name="chargeType"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="radio"
                      checked={field.value === "percentage"}
                      onChange={() => field.onChange("percentage")}
                      className={styles.radioInput}
                    />
                  )}
                />
                <span className={styles.radioText}>Percentage</span>
              </label>
            </div>
            
            {watchChargeType === "percentage" && (
              <Controller
                name="percentageCharge"
                control={control}
                rules={{ 
                  required: "Percentage is required for percentage charges",
                  pattern: {
                    value: numberRegex,
                    message: "Please enter a valid number"
                  }
                }}
                render={({ field, fieldState }) => (
                  <TextInput
                    label=""
                    value={field.value}
                    onChange={(val) => {
                      // Only allow numbers
                      if (val === "" || numberRegex.test(val)) {
                        field.onChange(val)
                      }
                    }}
                    placeholder="Enter percentage"
                    error={fieldState.error?.message ?? ""}
                    type="text"
                  />
                )}
              />
            )}
            
            {watchChargeType === "fixed" && (
              <Controller
                name="fixedCharge"
                control={control}
                rules={{ 
                  required: "Amount is required for fixed charges",
                  pattern: {
                    value: numberRegex,
                    message: "Please enter a valid number"
                  }
                }}
                render={({ field, fieldState }) => (
                  <TextInput
                    label=""
                    value={field.value}
                    onChange={(val) => {
                      // Only allow numbers
                      if (val === "" || numberRegex.test(val)) {
                        field.onChange(val)
                      }
                    }}
                    placeholder="Enter Amount"
                    error={fieldState.error?.message ?? ""}
                    type="text"
                  />
                )}
              />
            )}
          </div>

          <div className={styles.formDivider}></div>

          <div className={`${styles.formSection} ${styles.row}`}>
            <span className={styles.sectionLabel}>Tariff</span>
            <div className={`${styles.radioGroup} ${styles.row}`}>
              <label className={styles.radioLabel}>
                <Controller
                  name="tariff"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="radio"
                      checked={field.value === "Postpaid"}
                      onChange={() => field.onChange("Postpaid")}
                      className={styles.radioInput}
                    />
                  )}
                />
                <span className={styles.radioText}>Postpaid</span>
              </label>
              <label className={styles.radioLabel}>
                <Controller
                  name="tariff"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="radio"
                      checked={field.value === "Prepaid"}
                      onChange={() => field.onChange("Prepaid")}
                      className={styles.radioInput}
                    />
                  )}
                />
                <span className={styles.radioText}>Prepaid</span>
              </label>
              <label className={styles.radioLabel}>
                <Controller
                  name="tariff"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="radio"
                      checked={field.value === "Both"}
                      onChange={() => field.onChange("Both")}
                      className={styles.radioInput}
                    />
                  )}
                />
                <span className={styles.radioText}>Both</span>
              </label>
            </div>
          </div>

          <div className={styles.formDivider}></div>

          <div className={`${styles.formSection} ${styles.row}`}>
            <span className={styles.sectionLabel}>Select Country</span>
            {countriesLoading ? (
              <div className={styles.loading}>Loading countries...</div>
            ) : (
              <>
                <div className={styles.countryGrid}>
                  {countries.map((country: Country) => (
                    <div key={country.id} className={styles.countryItem}>
                      <div className={styles.countryInfo}>
                        <img
                          alt={`${country.name} flag`}
                          className={styles.countryFlag}
                          src={country.imageUrl || "/placeholder.svg"}
                        />
                        <span className={styles.countryName}>
                          {country.name} ({country.isoCode})
                        </span>
                      </div>
                      <ToggleSlider
                        isActive={watchCountryId === country.id}
                        onChange={() => handleCountrySelect(country.id)}
                      />
                    </div>
                  ))}
                </div>
                {!watchCountryId && errors.countryId && (
                  <div className={styles.errorText}>Please select a country</div>
                )}
              </>
            )}
          </div>
        </div>

        <div className={styles.actions}>
          <Button disabled={isSaving} text="Cancel Setup" variant="outline" type="button" onClick={onCancel} />
          <Button disabled={isSaving} text={isSaving? "Editing...": "Edit"} variant="secondary" type="submit" />
        </div>
      </form>
    </div>
  )
}

export default UtilityForm